<template>
  <div class="col-12 col-xl-6">
    <div ref="card" class="card h-100 border-translucent card-hover">
      <div class="card-body d-flex flex-column">
        <div class="d-flex align-items-start mb-3">
          <div v-if="icon" class="flex-shrink-0 me-3">
            <div
              class="icon-wrapper d-flex align-items-center justify-content-center"
            >
              <font-awesome-icon :icon="icon" class="text-primary" size="lg" />
            </div>
          </div>
          <div class="flex-grow-1">
            <h5 class="card-title mb-1">{{ title }}</h5>
            <p class="card-text text-muted small mb-2">
              {{ description }}
            </p>
            <slot name="badge"></slot>
          </div>
          <button
            v-if="fullscreenable && isSupported"
            type="button"
            class="btn btn-link p-0 flex-shrink-0 ms-2 card-icon-button"
            :aria-label="
              isFullscreen
                ? $t('market_insights.exit_fullscreen')
                : $t('market_insights.enter_fullscreen')
            "
            @click="toggle"
          >
            <font-awesome-icon
              :icon="
                isFullscreen
                  ? 'fa-solid fa-compress-alt'
                  : 'fa-solid fa-expand-arrows-alt'
              "
            />
          </button>
          <div v-if="infoText" class="flex-shrink-0 ms-2">
            <InfoTooltip :text="infoText" :showIcon="false">
              <font-awesome-icon
                icon="fa-solid fa-circle-info"
                class="info-icon"
              />
            </InfoTooltip>
          </div>
        </div>

        <slot name="content"></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import { useFullscreen } from "@vueuse/core";
import { useTemplateRef } from "vue";

defineProps<{
  title: string;
  description: string;
  icon?: string;
  infoText?: string;
  /** Shows a button that puts the whole card in browser full screen, where a chart in it grows to fill the screen. */
  fullscreenable?: boolean;
}>();

const { isFullscreen, isSupported, toggle } = useFullscreen(
  useTemplateRef<HTMLElement>("card")
);
</script>

<style scoped lang="scss">
.card-hover {
  transition: all 0.2s ease-in-out;
  cursor: default;
}

// In full screen the card fills the screen; let the chart take the height left under the controls
// instead of its fixed height prop (Chart.js resizes itself to its wrapper).
.card:fullscreen {
  border-radius: 0;
  overflow-y: auto;

  :deep(.chart-wrapper) {
    flex: 1 1 auto;
    height: auto !important;
    min-height: 0;
  }
}

.card-icon-button {
  font-size: 1.25rem;
  line-height: 1;
  color: rgba(var(--phoenix-body-color-rgb), 0.3);
  transition: color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: rgba(var(--phoenix-body-color-rgb), 0.6);
  }
}

.icon-wrapper {
  width: 40px;
  height: 40px;
  background-color: rgba(var(--phoenix-primary-rgb), 0.1);
  border-radius: 0.5rem;
}

.info-icon {
  cursor: help;
  font-size: 1.5rem;
  color: rgba(var(--phoenix-body-color-rgb), 0.3);
  transition: color 0.2s ease;

  &:hover {
    color: rgba(var(--phoenix-body-color-rgb), 0.6);
  }
}
</style>
